import { PaymentIcons, Radio, Text, useIsDesignLanguage, DesignLanguage, Heading } from '@afterpaytouch/core'
import React, { FC, useMemo } from 'react'
import { useCheckoutSizes } from '../../utils/responsive'
import styles from './style.module.css'
import { getCardBrandFromCardNumber } from '@afterpaytouch/form/lib/fields/Card/utils'
import { useTranslation } from '../../utils/convergence'
import { NewPaymentMethodFormSchema, SupportedNewPaymentType } from './types'
import { CardBrand, Feature } from '@afterpay/types'
import { PaymentIcons as PaymentIconsExperiment } from './PaymentIcons'
import { RoundIcon } from './RoundIcon'
import { useIsApplePaylater, useIsApplePaylaterWithApplePayRepayment } from '../../state/applePaylater/hooks'
import { TFunction } from 'i18next'

interface CardPaymentOptionProps {
  formData: NewPaymentMethodFormSchema
  selectedPaymentMethod: SupportedNewPaymentType
  setPaymentMethod: (method: SupportedNewPaymentType) => void
  feature: Feature
  enabledCardTypes: CardBrand[]
  closeApplepayTooltip: () => void
}

interface CardPaymentOptionLabelProps {
  selectedPaymentMethod: SupportedNewPaymentType
  feature: Feature
  enabledCardTypes: CardBrand[]
  selectedCardBrand?: CardBrand
}

type CardTypeFeature = Feature.PBI | Feature.PCL | Feature.AFF

type CardTypeLabelSelector = (t: TFunction) => string

const CARD_TYPE_LABEL_SELECTORS: Record<CardTypeFeature, CardTypeLabelSelector> = {
  [Feature.PBI]: (t) => t('paymentMethod:cardType.PBI'),
  [Feature.PCL]: (t) => t('paymentMethod:cardType.PCL'),
  [Feature.AFF]: (t) => t('paymentMethod:cardType.AFF'),
}
const DEFAULT_CARD_TYPE_LABEL_SELECTOR = CARD_TYPE_LABEL_SELECTORS[Feature.PBI]

const getCardTypeLabel = (feature: Feature, t: TFunction): string => {
  const cardTypeLabelSelector = CARD_TYPE_LABEL_SELECTORS[feature as CardTypeFeature] ?? DEFAULT_CARD_TYPE_LABEL_SELECTOR
  return cardTypeLabelSelector(t)
}

export const CardPaymentOptionLabelRedesigned: FC<CardPaymentOptionLabelProps> = ({ feature, enabledCardTypes }) => {
  const { headingSize, textSize } = useCheckoutSizes()
  const { t } = useTranslation()
  const isApplePaylater = useIsApplePaylater()
  const isApplePayLaterApplePayRepaymentEnabled = useIsApplePaylaterWithApplePayRepayment()
  const cardTypeLabel = getCardTypeLabel(feature, t)

  if (isApplePaylater && isApplePayLaterApplePayRepaymentEnabled) {
    if (feature === Feature.PBI) {
      return (
        <div>
          <Heading size={headingSize}>{t('paymentMethod:addPaymentMethod.applePayLater.heading.PBI')}</Heading>
          <Text size={textSize} className={styles.applePayLaterText}>
            {t('paymentMethod:addPaymentMethod.applePayLater.description')}
          </Text>
        </div>
      )
    }

    if (feature === Feature.PCL) {
      return (
        <div>
          <Heading size={headingSize}>{t('paymentMethod:addPaymentMethod.applePayLater.heading.PCL')}</Heading>
          <div className={styles.applePayLaterAcceptableCardsContainer}>
            <Text renderAs='span' size={textSize} color='Subtle'>
              {t('paymentMethod:weAccept')}
            </Text>
            <PaymentIconsExperiment allowedCardBrand={enabledCardTypes} />
          </div>
        </div>
      )
    }
  }

  return (
    <div className={styles.cardLabelContainer}>
      <RoundIcon name='CardAdd' size='Medium' color='Subtle' />
      <div className={styles.cardLabelHeadingContainer}>
        <Text renderAs='span' bold size={textSize} testNameSpace='payment-method-heading'>
          {cardTypeLabel}
        </Text>
        <div className={styles.acceptableCardsContainer}>
          <Text renderAs='span' size={textSize} color='Subtle'>
            {t('paymentMethod:weAccept')}
          </Text>
          <PaymentIconsExperiment allowedCardBrand={enabledCardTypes} />
        </div>
      </div>
    </div>
  )
}

export const CardPaymentOption: FC<CardPaymentOptionProps> = ({
  formData,
  selectedPaymentMethod,
  setPaymentMethod,
  feature,
  enabledCardTypes,
  closeApplepayTooltip,
}) => {
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const selectedCardBrand = useMemo(() => getCardBrandFromCardNumber(formData.cardField.cardNumber), [formData.cardField.cardNumber])
  const { radioSize } = useCheckoutSizes()
  return (
    <Radio.Button
      labelPlacement={isArcadeDesignLanguage ? 'Top' : 'Middle'}
      labelWidth='Full'
      size={radioSize}
      indent={!isArcadeDesignLanguage}
      icon={isArcadeDesignLanguage ? 'Default' : 'Select'}
      value='card'
      checked={selectedPaymentMethod === 'card'}
      kind={isArcadeDesignLanguage ? 'Option' : 'Default'}
      reversed={isArcadeDesignLanguage}
      label={() =>
        isArcadeDesignLanguage ? (
          <CardPaymentOptionLabelRedesigned selectedPaymentMethod={selectedPaymentMethod} feature={feature} enabledCardTypes={enabledCardTypes} />
        ) : (
          <CardPaymentOptionLabel
            selectedPaymentMethod={selectedPaymentMethod}
            feature={feature}
            enabledCardTypes={enabledCardTypes}
            selectedCardBrand={selectedCardBrand}
          />
        )
      }
      onChange={() => {
        setPaymentMethod('card')
        closeApplepayTooltip()
      }}
      testNameSpace='card'
    />
  )
}

const CardPaymentOptionLabel: FC<CardPaymentOptionLabelProps> = ({ selectedPaymentMethod, feature, enabledCardTypes, selectedCardBrand }) => {
  const { textSize } = useCheckoutSizes()
  const { t } = useTranslation()
  const cardTypeLabel = getCardTypeLabel(feature, t)
  return (
    <div className={styles.heading}>
      <Text renderAs='span' bold={selectedPaymentMethod === 'card'} size={textSize} testNameSpace='payment-method-heading'>
        {cardTypeLabel}
      </Text>
      <PaymentIcons allowedCardBrand={enabledCardTypes} selectedCardBrand={selectedCardBrand} />
    </div>
  )
}
